import { Link } from "react-router-dom";
import { BarChart3, ArrowRight, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAnalyticsSummary } from "@/hooks/use-analytics";
import { isReleaseFlagOn } from "@/config/featureFlags";
import type { AnalyticsSummaryResponse, OfferingTypeMetrics } from "@/services/analyticsService";
import { TierHero } from "./TierHero";
import type { KpiTileProps } from "./KpiTile";
import { UnreadInquiriesCallout } from "./UnreadInquiriesCallout";
import { UnifiedCard } from "./UnifiedCard";

export interface DashboardAnalyticsCardProps {
  className?: string;
  noDivider?: boolean;
}

/**
 * D5: same default offering-type selection rule as the Insights page — whichever of
 * `oneToOne`/`group` is present; if only one is present use it; if both are present,
 * pick whichever has the higher `(totalImpressions + totalInquiries)`, tie-break
 * ONE_TO_ONE.
 *
 * When neither is present, `AnalyticsSummaryResponse` alone doesn't give this card
 * enough signal to distinguish "crash-course-only" from "genuinely no offerings yet"
 * (that richer per-offering breakdown only exists on the Insights page's
 * `AnalyticsInsightsResponse`). This smaller-surface dashboard card treats both cases
 * the same: placeholder "—" KPIs with `isCrashCourseOnly=false`, letting `TierHero`'s
 * NEWCOMER / not-enough-cohort-data sentence branches explain the empty state instead.
 */
function selectOfferingType(
  oneToOne: OfferingTypeMetrics | null,
  group: OfferingTypeMetrics | null
): OfferingTypeMetrics | null {
  if (oneToOne && group) {
    const oneToOneScore = oneToOne.totalImpressions + oneToOne.totalInquiries;
    const groupScore = group.totalImpressions + group.totalInquiries;
    return groupScore > oneToOneScore ? group : oneToOne;
  }
  return oneToOne ?? group ?? null;
}

/**
 * F17 (AC#28): builds a one-row-per-metric CSV of the currently displayed headline
 * numbers. Platinum-only (see `isPlatinum` in the component below; the trend arrows
 * are a separate, Gold+ entitlement -- see `trendsVisible`) -- keyed off
 * `summary.subscriptionTier`, NEVER off `TierHero`'s `tier` prop (a different,
 * unrelated `PerformanceTier` concept -- see the D5 comment above and
 * `TeacherAnalyticsPage.tsx`).
 */
function buildAnalyticsCsv(summary: AnalyticsSummaryResponse, offering: OfferingTypeMetrics | null): string {
  const rows: [string, string][] = [
    ["Period (days)", String(summary.periodDays)],
    ["Sent you an inquiry", offering ? String(offering.totalInquiries) : "—"],
    ["How often you reply", offering ? `${(offering.responseRate * 100).toFixed(1)}%` : "—"],
    ["Times students saw you", offering ? String(offering.totalImpressions) : "—"],
    ["Turned into profile views", offering && offering.totalImpressions > 0
      ? `${(offering.clickThroughRate * 100).toFixed(1)}%`
      : "N/A"],
  ];
  const escape = (cell: string) => `"${cell.replace(/"/g, '""')}"`;
  return [["Metric", "Value"], ...rows].map((row) => row.map(escape).join(",")).join("\n");
}

function downloadAnalyticsCsv(summary: AnalyticsSummaryResponse, offering: OfferingTypeMetrics | null): void {
  const csv = buildAnalyticsCsv(summary, offering);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "bmc-analytics-summary.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Self-contained teacher-dashboard analytics card (T-F13). Owns its own data fetch via
 * `useAnalyticsSummary()` (D12) so a failure here never takes down the rest of the
 * dashboard — loading/error states are scoped entirely to this card, not the page.
 */
export function DashboardAnalyticsCard({ className, noDivider }: DashboardAnalyticsCardProps) {
  const { data: summary, isLoading, isError, refetch } = useAnalyticsSummary();

  if (isLoading) {
    return (
      <UnifiedCard icon={BarChart3} title="Your Analytics" className={className} noDivider={noDivider}>
        <div className="animate-pulse space-y-4" aria-hidden="true">
          <div className="h-4 w-40 rounded bg-muted" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="h-24 rounded-xl bg-muted" />
            <div className="h-24 rounded-xl bg-muted" />
          </div>
        </div>
        <span className="sr-only">Loading your analytics…</span>
      </UnifiedCard>
    );
  }

  if (isError) {
    return (
      <UnifiedCard icon={BarChart3} title="Your Analytics" className={className} noDivider={noDivider}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">Couldn't load your analytics.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-sm font-semibold text-primary hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
      </UnifiedCard>
    );
  }

  if (!summary) {
    return null;
  }

  const offering = selectOfferingType(summary.oneToOne, summary.group);

  // F17 (AC#28): the CSV export below is a Platinum-only benefit. Keyed off
  // `summary.subscriptionTier` -- NEVER off `summary.tier` (the unrelated
  // `PerformanceTier` passed to `TierHero` below) -- and gated behind the release
  // flag so this is inert until the flag is on. Trend indicators used to share this
  // gate; ADR-140 moved them to Gold+ and to the server -- see `trendsVisible`.
  const tierBenefitsOn = isReleaseFlagOn('TEACHER_TIER_BENEFITS');
  const isPlatinum = tierBenefitsOn && summary.subscriptionTier === 'PLATINUM';

  // ADR-140 moved period-over-period from Platinum to GOLD+, and moved the decision itself to the
  // server: `summary.periodComparisonVisible`, with `prior` withheld from the payload below Gold.
  // Still combined with the release flag here because the flag being off means the server returns
  // `prior` to everyone (its pre-feature behaviour), and this card showed trends to nobody before
  // the flag existed -- keying on the field alone would light trends up for every tier while the
  // flag is off.
  const trendsVisible = tierBenefitsOn && summary.periodComparisonVisible;

  const headlineKpis: [KpiTileProps, KpiTileProps] = offering
    ? [
        {
          label: "Sent you an inquiry",
          subcaption: "students who reached out",
          value: offering.totalInquiries,
          trend: trendsVisible && offering.prior
            ? { current: offering.totalInquiries, prior: offering.prior.totalInquiries }
            : undefined,
        },
        {
          label: "How often you reply",
          subcaption: "of students who reached out",
          value: `${(offering.responseRate * 100).toFixed(1)}%`,
          trend: trendsVisible && offering.prior
            ? { current: offering.responseRate, prior: offering.prior.responseRate }
            : undefined,
        },
      ]
    : [
        { label: "Sent you an inquiry", subcaption: "students who reached out", value: "—" },
        { label: "How often you reply", subcaption: "of students who reached out", value: "—" },
      ];

  return (
    <UnifiedCard icon={BarChart3} title="Your Analytics" className={className} noDivider={noDivider}>
      <div className="space-y-4">
        <UnreadInquiriesCallout unreadCount={summary.unreadInquiryCount} />
        <TierHero
          tier={summary.tier}
          cohortMinimumMet={summary.cohortMinimumMet}
          headlineKpis={headlineKpis}
          embedded
        />
        <div className={cn("flex items-center gap-2", isPlatinum ? "justify-between" : "justify-end")}>
          {isPlatinum && (
            <button
              type="button"
              onClick={() => downloadAnalyticsCsv(summary, offering)}
              className={cn(
                "inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground",
                "cursor-pointer motion-safe:transition-colors motion-safe:duration-200",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              )}
            >
              <Download className="w-3.5 h-3.5" aria-hidden="true" />
              Export CSV
            </button>
          )}
          <Link
            to="/teacher-analytics"
            className={cn(
              "inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            )}
          >
            View full insights
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </UnifiedCard>
  );
}

export default DashboardAnalyticsCard;
